import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, GraduationCap, Briefcase, Paperclip, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ROLES = ["Carpenter", "Builders Labourer", "Project Manager"];
const OPEN_ROLES = [
  { role: "Carpenter", desc: "Qualified, proven track record, light commercial experience." },
  { role: "Builders Labourer", desc: "Minimum 2 years' experience on site." },
  { role: "Project Manager", desc: "Experience managing residential and light commercial builds." },
];
const FILE_EXTS = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const AVAILABILITY = ["Immediately", "2 weeks", "1 month", "Other"];

const inputCls = "w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors";
const labelCls = "block text-[10px] font-bold uppercase tracking-[0.25em] text-white/45 mb-2";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}>
    {children}
  </motion.div>
);

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <div className="flex gap-2">
        {["Yes", "No"].map((o) => (
          <button type="button" key={o} data-testid={`${testid}-${o.toLowerCase()}`} onClick={() => onChange(o)}
            className={`px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] border transition-colors duration-300 ${
              value === o ? "bg-gold text-black border-gold" : "border-white/15 text-white/60 hover:border-gold hover:text-gold"}`}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

const EMP_BLANK = {
  name: "", age: "", location: "", phone: "", email: "", role: "", about: "", why: "",
  licence: "", availability: AVAILABILITY[0],
  experience_years: "", trade_qualification: "", light_commercial: "",
  light_commercial_detail: "", lbp_number: "", right_to_work: "",
};

function EmploymentForm() {
  const [form, setForm] = useState(EMP_BLANK);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [progress, setProgress] = useState(0);
  const [sending, setSending] = useState(false);
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyFor = (role) => {
    setForm({ ...form, role });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const addFiles = (list) => {
    setFileError("");
    const next = [...files];
    for (const f of Array.from(list)) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!FILE_EXTS.includes(ext)) { setFileError(`${f.name} — only PDF, Word, JPG or PNG files are accepted.`); continue; }
      if (f.size > MAX_FILE_BYTES) { setFileError(`${f.name} — files must be 10 MB or smaller.`); continue; }
      if (next.length >= 3) { setFileError("Maximum 3 files."); break; }
      next.push({ id: `${Date.now()}-${next.length}`, file: f });
    }
    setFiles(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role) { toast.error("Please select a role."); return; }
    const missing = [];
    if (!form.experience_years.trim()) missing.push("years of experience");
    if (!form.trade_qualification.trim()) missing.push("trade qualification held");
    if (!form.light_commercial) missing.push("light commercial experience");
    if (form.light_commercial === "Yes" && !form.light_commercial_detail.trim()) missing.push("light commercial detail");
    if (!form.right_to_work) missing.push("right to work in NZ");
    if (!form.licence) missing.push("driver's licence");
    if (missing.length) { toast.error("Please complete: " + missing.join(", ") + "."); return; }
    setSending(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(form));
      files.forEach(({ file }) => fd.append("files", file));
      await api.post("/careers/employment", fd, {
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded * 100) / (ev.total || 1))),
      });
      toast.success("Application sent — we'll be in touch soon.");
      setForm(EMP_BLANK);
      setFiles([]);
      setProgress(0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* OPEN ROLES */}
      <div data-testid="open-roles" className="mb-12 grid sm:grid-cols-3 gap-4">
        {OPEN_ROLES.map((r) => (
          <div key={r.role} data-testid={`open-role-${r.role.toLowerCase().replace(/\s/g, "-")}`}
            className="border border-white/15 p-6 flex flex-col hover:border-gold/60 transition-colors duration-300">
            <h3 className="font-display font-semibold uppercase tracking-tight text-xl">{r.role}</h3>
            <p className="mt-3 text-sm text-white/55 leading-relaxed flex-1">{r.desc}</p>
            <button type="button" data-testid={`apply-${r.role.toLowerCase().replace(/\s/g, "-")}`} onClick={() => applyFor(r.role)}
              className="mt-6 w-fit px-6 py-2.5 border border-gold text-gold text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-gold hover:text-black transition-colors duration-300">
              Apply
            </button>
          </div>
        ))}
      </div>

      <form ref={formRef} onSubmit={submit} data-testid="employment-form" className="space-y-6 scroll-mt-40">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className={labelCls} htmlFor="emp-name">Full name *</label>
            <input id="emp-name" data-testid="emp-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="emp-role">What role are you interested in? *</label>
            <select id="emp-role" data-testid="emp-role-select" value={form.role} onChange={set("role")} className={`${inputCls} select-dark`}>
              <option value="" disabled>Select a role</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="emp-location">Location (town/suburb) *</label>
            <input id="emp-location" data-testid="emp-location-input" required value={form.location} onChange={set("location")} placeholder="e.g. Masterton" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="emp-phone">Phone number *</label>
            <input id="emp-phone" data-testid="emp-phone-input" required value={form.phone} onChange={set("phone")} placeholder="027 000 0000" className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="emp-email">Email address *</label>
            <input id="emp-email" data-testid="emp-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.co.nz" className={inputCls} />
          </div>
        </div>

        {/* EXPERIENCE — applies to all three roles */}
        <div className="border border-white/15 p-6 sm:p-8 space-y-6" data-testid="experience-block">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Experience &amp; qualifications</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className={labelCls} htmlFor="emp-experience">Years of experience *</label>
              <input id="emp-experience" data-testid="emp-experience-input" type="number" min="0" value={form.experience_years}
                onChange={set("experience_years")} placeholder="e.g. 5" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="emp-qualification">Trade qualification held *</label>
              <input id="emp-qualification" data-testid="emp-qualification-input" value={form.trade_qualification}
                onChange={set("trade_qualification")} placeholder="e.g. NZ Certificate in Carpentry / LBP" className={inputCls} />
            </div>
            <YesNo label="Light commercial experience *" value={form.light_commercial}
              onChange={(v) => setForm({ ...form, light_commercial: v })} testid="emp-light-commercial" />
            <div>
              <label className={labelCls} htmlFor="emp-lbp">Current LBP licence number (optional)</label>
              <input id="emp-lbp" data-testid="emp-lbp-input" value={form.lbp_number} onChange={set("lbp_number")}
                placeholder="e.g. BP 123456" className={inputCls} />
            </div>
          </div>
          {form.light_commercial === "Yes" && (
            <div>
              <label className={labelCls} htmlFor="emp-light-detail">Briefly describe your light commercial experience *</label>
              <input id="emp-light-detail" data-testid="emp-light-commercial-detail-input" value={form.light_commercial_detail}
                onChange={set("light_commercial_detail")} placeholder="e.g. 2 years on retail fit-outs" className={inputCls} />
            </div>
          )}
          <YesNo label="Right to work in NZ *" value={form.right_to_work}
            onChange={(v) => setForm({ ...form, right_to_work: v })} testid="emp-rtw" />
        </div>

        <div>
          <label className={labelCls} htmlFor="emp-about">Tell us a bit about yourself *</label>
          <textarea id="emp-about" data-testid="emp-about-input" required rows={4} value={form.about} onChange={set("about")}
            placeholder="Background, experience, what you're into…" className={`${inputCls} resize-none`} />
        </div>
        <div>
          <label className={labelCls} htmlFor="emp-why">Why do you want to work for LW&Co? (optional)</label>
          <textarea id="emp-why" data-testid="emp-why-input" rows={4} value={form.why} onChange={set("why")}
            placeholder="Tell us why LW&Co…" className={`${inputCls} resize-none`} />
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <YesNo label="Do you have a driver's licence? *" value={form.licence} onChange={(v) => setForm({ ...form, licence: v })} testid="emp-licence" />
          <div>
            <label className={labelCls} htmlFor="emp-availability">Are you available to start? *</label>
            <select id="emp-availability" data-testid="emp-availability-select" value={form.availability} onChange={set("availability")} className={`${inputCls} select-dark`}>
              {AVAILABILITY.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        {/* ATTACHMENTS */}
        <div data-testid="attachments-block">
          <span className={labelCls}>Attach your CV, trade certificates or licence (optional but encouraged)</span>
          <input ref={fileInputRef} data-testid="emp-file-input" type="file" multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          <button type="button" data-testid="emp-attach-btn" onClick={() => fileInputRef.current.click()}
            className="inline-flex items-center gap-2.5 px-6 py-3.5 border border-white/25 text-[11px] font-bold uppercase tracking-[0.2em] text-white/70 hover:border-gold hover:text-gold transition-colors duration-300">
            <Paperclip size={15} /> Attach files
          </button>
          <p className="mt-2.5 text-xs text-white/40">PDF, Word, JPG or PNG — up to 3 files, 10 MB each.</p>
          {fileError && <p data-testid="emp-file-error" className="mt-3 text-sm text-red-400">{fileError}</p>}
          {files.length > 0 && (
            <ul data-testid="emp-file-list" className="mt-4 space-y-2">
              {files.map(({ id, file }) => (
                <li key={id} data-testid={`emp-file-${id}`}
                  className="flex items-center justify-between gap-4 border border-white/15 px-4 py-3">
                  <span className="text-sm text-white/75 truncate">{file.name}
                    <span className="text-white/35 text-xs ml-2">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                  </span>
                  <button type="button" data-testid={`emp-file-remove-${id}`} aria-label={`Remove ${file.name}`}
                    onClick={() => setFiles(files.filter((f) => f.id !== id))}
                    className="p-1.5 text-white/40 hover:text-red-400 transition-colors shrink-0">
                    <X size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {sending && progress > 0 && (
          <div data-testid="emp-upload-progress">
            <div className="flex justify-between text-[10px] font-bold uppercase tracking-[0.25em] text-white/45 mb-2">
              <span>Uploading</span><span>{progress}%</span>
            </div>
            <div className="h-1.5 bg-white/10 w-full">
              <div className="h-full bg-gold transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <button data-testid="employment-submit-btn" type="submit" disabled={sending}
          className="group inline-flex items-center gap-2 px-10 py-4 bg-gold text-black font-bold text-sm uppercase tracking-[0.15em] hover:bg-white disabled:opacity-50 transition-colors duration-300">
          {sending ? "Sending…" : "Submit Application"} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </button>
        <p data-testid="emp-privacy-note" className="text-xs text-white/40">
          Your details and documents are only used to assess your application.
        </p>
      </form>
    </div>
  );
}

const APP_BLANK = { name: "", dob: "", address: "", phone: "", email: "", emergency_contact: "", emergency_phone: "", finished_school: "", qualification: "", trade_experience: "", trade_experience_detail: "", why_carpenter: "", licence: "", nz_citizen: "", declaration: false };

function ApprenticeshipForm() {
  const [form, setForm] = useState(APP_BLANK);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    for (const [k, label] of [["finished_school", "whether you've finished school"], ["trade_experience", "any trade work history"], ["licence", "your driver's licence status"], ["nz_citizen", "your citizenship/residency status"]]) {
      if (!form[k]) { toast.error(`Please tell us ${label}.`); return; }
    }
    if (!form.declaration) { toast.error("Please confirm the declaration to submit."); return; }
    setSending(true);
    try {
      await api.post("/careers/apprenticeship", form);
      toast.success("Enrolment sent — we'll be in touch about next steps.");
      setForm(APP_BLANK);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="apprenticeship-form" className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className={labelCls} htmlFor="app-name">Full name *</label>
          <input id="app-name" data-testid="app-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="app-dob">Date of birth *</label>
          <input id="app-dob" data-testid="app-dob-input" required type="date" value={form.dob} onChange={set("dob")} className={`${inputCls} [color-scheme:dark]`} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="app-address">Address *</label>
          <input id="app-address" data-testid="app-address-input" required value={form.address} onChange={set("address")} placeholder="Street, town, postcode" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="app-phone">Phone number *</label>
          <input id="app-phone" data-testid="app-phone-input" required value={form.phone} onChange={set("phone")} placeholder="027 000 0000" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="app-email">Email address *</label>
          <input id="app-email" data-testid="app-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.co.nz" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="app-emergency-name">Emergency contact name *</label>
          <input id="app-emergency-name" data-testid="app-emergency-name-input" required value={form.emergency_contact} onChange={set("emergency_contact")} placeholder="Contact person" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="app-emergency-phone">Emergency contact phone *</label>
          <input id="app-emergency-phone" data-testid="app-emergency-phone-input" required value={form.emergency_phone} onChange={set("emergency_phone")} placeholder="027 000 0000" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="app-qualification">Highest qualification held *</label>
          <input id="app-qualification" data-testid="app-qualification-input" required value={form.qualification} onChange={set("qualification")} placeholder="e.g. NCEA Level 2" className={inputCls} />
        </div>
        <YesNo label="Have you finished school? *" value={form.finished_school} onChange={(v) => setForm({ ...form, finished_school: v })} testid="app-school" />
      </div>
      <div>
        <YesNo label="Have you done any building/trade work before? *" value={form.trade_experience} onChange={(v) => setForm({ ...form, trade_experience: v })} testid="app-trade" />
        {form.trade_experience === "Yes" && (
          <textarea data-testid="app-trade-detail-input" rows={3} value={form.trade_experience_detail} onChange={set("trade_experience_detail")}
            placeholder="Tell us about it — what kind of work, who for, how long…" className={`${inputCls} resize-none mt-4`} />
        )}
      </div>
      <div>
        <label className={labelCls} htmlFor="app-why">Why do you want to become a carpenter? *</label>
        <textarea id="app-why" data-testid="app-why-input" required rows={4} value={form.why_carpenter} onChange={set("why_carpenter")}
          placeholder="What draws you to the trade…" className={`${inputCls} resize-none`} />
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <YesNo label="Do you have a driver's licence? *" value={form.licence} onChange={(v) => setForm({ ...form, licence: v })} testid="app-licence" />
        <YesNo label="Are you a NZ citizen or resident? *" value={form.nz_citizen} onChange={(v) => setForm({ ...form, nz_citizen: v })} testid="app-citizen" />
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="app-declaration-label">
        <input type="checkbox" data-testid="app-declaration-checkbox" checked={form.declaration}
          onChange={(e) => setForm({ ...form, declaration: e.target.checked })}
          className="mt-1 h-4 w-4 shrink-0 accent-[#C9A96E]" />
        <span className="text-sm text-white/70 leading-relaxed">I confirm the information provided is true and accurate.</span>
      </label>
      <button data-testid="apprenticeship-submit-btn" type="submit" disabled={sending}
        className="group inline-flex items-center gap-2 px-10 py-4 bg-gold text-black font-bold text-sm uppercase tracking-[0.15em] hover:bg-white disabled:opacity-50 transition-colors duration-300">
        {sending ? "Sending…" : "Submit Enrolment"} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
      </button>
    </form>
  );
}

export default function Careers() {
  return (
    <main data-testid="careers-page" className="bg-black text-white">
      {/* HERO */}
      <section className="pt-56 pb-20 sm:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 80% 0%, rgba(201,169,110,0.12) 0%, transparent 55%)" }} />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">Careers &amp; Apprenticeships</motion.p>
          <div className="overflow-hidden">
            <motion.h1 initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
              data-testid="careers-headline">
              Join the LW&Co team
            </motion.h1>
          </div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-8">
            <p className="max-w-xl text-base sm:text-lg text-white/65 leading-relaxed" data-testid="careers-subtext">
              We are always on the lookout for hardworking, passionate people to join our whānau
              in Masterton, Wairarapa.
            </p>
            <span data-testid="bcito-badge"
              className="mt-8 inline-flex items-center gap-2.5 px-5 py-2.5 border border-gold/60 text-gold text-[11px] font-bold uppercase tracking-[0.25em]">
              <GraduationCap size={15} /> Proud BCITO Employer
            </span>
          </motion.div>
        </div>
      </section>

      {/* EMPLOYMENT APPLICATION */}
      <section className="py-20 sm:py-24 border-t border-white/10" data-testid="employment-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <Reveal className="mb-12">
            <div className="flex items-center gap-4 mb-5">
              <Briefcase size={22} className="text-gold" strokeWidth={1.5} />
              <span className="text-[11px] font-bold tracking-[0.3em] text-gold">01</span>
            </div>
            <h2 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-5xl">
              Employment Application
            </h2>
            <p className="mt-4 text-sm sm:text-base text-white/50 leading-relaxed max-w-xl">
              Qualified carpenter, labourer or keen to get started — tell us who you are and
              where you want to go.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <EmploymentForm />
          </Reveal>
        </div>
      </section>

      {/* APPRENTICESHIP ENROLMENT */}
      <section className="py-20 sm:py-24 border-t border-white/10" data-testid="apprenticeship-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <Reveal className="mb-12">
            <div className="flex items-center gap-4 mb-5">
              <GraduationCap size={22} className="text-gold" strokeWidth={1.5} />
              <span className="text-[11px] font-bold tracking-[0.3em] text-gold">02</span>
            </div>
            <h2 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-5xl">
              Apprenticeship Enrolment
            </h2>
            <p className="mt-4 text-sm sm:text-base text-white/50 leading-relaxed max-w-xl">
              Start your carpentry apprenticeship with LW&Co through BCITO. Fill in your details
              and we'll walk you through the enrolment.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ApprenticeshipForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
